<template>
  <NuxtLink :to="to" class="gr-card">
    <div class="gr-card__media">
      <img
        v-if="hasBanner && !hasImageError"
        :ref="setImageElement"
        :src="contentImg"
        :alt="contentTitle"
        class="gr-card__img"
        loading="lazy"
        decoding="async"
        @error="hasImageError = true"
      />
      <!-- A missing or broken banner lands on the same neutral stand-in, and
           the stand-in carries no alt text because it says nothing about the
           activity. -->
      <div v-else class="gr-card__img gr-card__img--fallback">
        <v-icon icon="mdi-image-off-outline" size="28" aria-hidden="true" />
      </div>
    </div>

    <div class="gr-card__body">
      <component :is="titleTag" class="gr-card__title">
        {{ contentTitle }}
      </component>
      <p class="gr-card__date">{{ contentDate }}</p>
      <div class="gr-card__meta">
        <span class="gr-card__meta-item">
          <v-icon
            icon="mdi-calendar-check-outline"
            size="14"
            aria-hidden="true"
          />
          {{ statusLabel }}
        </span>
        <span class="gr-card__meta-item">
          <v-icon icon="mdi-map-marker" size="14" aria-hidden="true" />
          {{ contentCity }}
        </span>
      </div>
    </div>
  </NuxtLink>
</template>

<script lang="ts" setup>
import { ACTIVITY_STATUS_LABEL } from "@/common/utils/activityStatus";
import type { ActivityStatus } from "@/common/utils/activityStatus";

interface ActivityCardProps {
  to: string;
  contentImg: string;
  contentTitle: string;
  contentDate: string;
  contentCity: string;
  /** Heading depth of the card title, so the page ladder never skips a level. */
  headingLevel?: 2 | 3;
  contentStatus?: ActivityStatus;
}

const props = withDefaults(defineProps<ActivityCardProps>(), {
  headingLevel: 3,
  contentStatus: "FINISHED",
});

const hasImageError = ref(false);
const hasBanner = computed(() => Boolean(props.contentImg));
const titleTag = computed(() => `h${props.headingLevel}`);

// Typed lookup: a status without a label is caught by `pnpm type-check`, so a
// raw status code can never reach a visitor.
const statusLabel = computed(() => ACTIVITY_STATUS_LABEL[props.contentStatus]);

// A banner that already failed before hydration never emits `error`.
const imageElement = ref<HTMLImageElement | null>(null);
const setImageElement = (
  element: Element | ComponentPublicInstance | null,
): void => {
  imageElement.value = element instanceof HTMLImageElement ? element : null;
};
onMounted(() => {
  const image = imageElement.value;
  if (image?.complete && image.naturalWidth === 0) hasImageError.value = true;
});
</script>

<style scoped>
.gr-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid var(--gr-line);
  border-radius: 4px;
  background-color: var(--gr-surface);
  color: inherit;
  text-decoration: none;
  transition:
    border-color 180ms cubic-bezier(0.2, 0, 0, 1),
    background-color 180ms cubic-bezier(0.2, 0, 0, 1);
}

@media (hover: hover) {
  .gr-card:hover {
    border-color: var(--gr-green-deep);
  }

  .gr-card:hover .gr-card__title {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

.gr-card__media {
  /* The tinted bed doubles as the loading surface: it is painted before the
     image resolves and stays behind a transparent one. */
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 4px 4px 0 0;
  background-color: var(--gr-green-tint);
}

.gr-card__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gr-card__img--fallback {
  display: grid;
  place-items: center;
  color: var(--gr-ink-3);
}

.gr-card__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 16px;
}

.gr-card__title {
  display: -webkit-box;
  margin: 0;
  color: var(--gr-ink);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
  /* Two lines reserved in em so the box grows with zoom instead of clipping. */
  min-height: 2.8em;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gr-card__date {
  margin: 8px 0 0;
  color: var(--gr-ink-3);
  font-size: 0.8125rem;
  line-height: 1.4;
}

.gr-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: auto;
  padding-top: 12px;
}

.gr-card__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  color: var(--gr-ink-3);
  font-size: 0.8125rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
</style>
